import type { ReactNode } from "react";

import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  AssetGroup,
  Hoid,
  HoidPartner,
  ProgramDetailPatch,
  ProgramDetailPatchState,
} from "../data";
import type { DateRange } from "./dateRangePresets";

import { BottomSheet } from "../../../BottomSheet";
import { Button as ButtonV2 } from "../../../Button";
import { CopyButton } from "../../../CopyButton";
import { ArrowRightIcon, BaseArchiveIcon, ChartLineIcon } from "../../../Icons";
import { InlineNotification } from "../../../InlineNotification";
import { SkeletonLoader } from "../../../SkeletonLoader";
import { StatusPill } from "../../../StatusPill";
import { TooltipWrapper } from "../../../Tooltip";
import { Typo } from "../../../Typo";
import {
  fmtDate,
  fmtInt,
  fmtVolExact,
  fmtVolMln,
  hasUndismissedRejection,
  isRecentlyAdded,
  programDisplayStatus,
} from "../data";
import { RebateSplitOverrideSection } from "../RebateSplitOverrideSection";
import { SubIbStatusTag } from "../SubIbStatusTag";
import { useDateRangePresets } from "./dateRangePresets";
import {
  MobileDateRangeChip,
  MobileDateRangeChipSheets,
} from "./MobileDateRangeChip";
import { MobileFieldRow } from "./MobileFieldRow";
import { MobileInfoTrigger } from "./MobileInfoTrigger";

const PerformanceFieldSkeleton = () => (
  <span data-testid="mobile-performance-field-loading">
    <SkeletonLoader height={18} width={72} variant="rounded" />
  </span>
);

/**
 * Mirrors the desktop drawer's field-level scoped-skeleton helper
 * (`ProgramDetailsDrawer.tsx`'s `renderMoneyField`) — including the same
 * exact-figure tap tooltip once the resolved value is shortened ("mln"/"bln").
 */
const renderPerformanceField = (
  fallback: number,
  patchState: ProgramDetailPatchState | undefined,
  pick: (patch: ProgramDetailPatch) => number,
  format: (n: number) => string,
): ReactNode => {
  const resolved = !patchState
    ? fallback
    : patchState.status === "success"
      ? pick(patchState.value)
      : null;
  if (resolved === null) {
    return patchState?.status === "loading" ? (
      <PerformanceFieldSkeleton />
    ) : (
      "—"
    );
  }
  const formatted = format(resolved);
  if (Math.abs(resolved) < 1e6) return formatted;
  return (
    <TooltipWrapper
      text={fmtVolExact(resolved)}
      color="blue"
      openEvents={{ click: true }}
      // Matches the desktop drawer's renderMoneyField: without "fixed" the
      // tooltip panel can get clipped/mispositioned by the sheet's own
      // scrollable body.
      positionStrategy="fixed"
      tooltipPlacement="top"
      tooltipMaxWidth={240}
      tooltipContainerClassName="!text-xs"
    >
      <Typo element="span" type="bodySM" textColor="text-text-primary">
        {formatted}
      </Typo>
    </TooltipWrapper>
  );
};

const SPLIT_COLORS = [
  "bg-surface-brand-default",
  "bg-surface-emphasis",
  "bg-surface-accent-blue-subtler",
];

const DrawerErrorBody = ({ onRetry }: { onRetry?: () => void }) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-col items-center justify-center gap-6 py-16 text-center"
      role="alert"
      data-testid="mobile-program-detail-error"
    >
      <div className="flex flex-col items-center gap-2">
        <Typo element="span" type="labelMD" textColor="text-text-primary">
          {t("subIBReport_detailErrorTitle", "Unable to load details")}
        </Typo>
        <Typo element="span" type="bodySM" textColor="text-text-secondary">
          {t(
            "subIBReport_detailErrorDescription",
            "Something went wrong. Refresh to try again.",
          )}
        </Typo>
      </div>
      {onRetry && (
        <ButtonV2 variant="brand" size="md" onClick={onRetry}>
          {t("subIBReport_errorRetry", "Refresh")}
        </ButtonV2>
      )}
    </div>
  );
};

/**
 * One label→value skeleton row, sized to stand in for a real
 * `MobileFieldRow` (py-2). Takes the same `className` escape hatch as
 * `MobileFieldRow` itself — callers must pass the same `!pt-0`/`!pb-0`
 * boundary overrides to a group's first/last row as the real rows use, or
 * the skeleton renders 8px taller at each end and the layout shifts once
 * real data replaces it.
 */
const SkeletonFieldRow = ({
  labelWidth,
  className,
}: {
  labelWidth: number;
  className?: string;
}) => (
  <div
    className={clsx("flex items-center justify-between gap-2 py-2", className)}
  >
    <SkeletonLoader height={14} width={labelWidth} variant="rounded" />
    <SkeletonLoader height={14} width={64} variant="rounded" />
  </div>
);

/** One split-table data row (IB / Split % / Payout columns). */
const SkeletonSplitRow = ({ nameWidth }: { nameWidth: number }) => (
  <div className="grid grid-cols-[minmax(0,1fr)_52px_100px] items-center gap-4">
    <SkeletonLoader height={14} width={nameWidth} variant="rounded" />
    <SkeletonLoader
      height={14}
      width={32}
      variant="rounded"
      className="ml-auto"
    />
    <SkeletonLoader
      height={14}
      width={48}
      variant="rounded"
      className="ml-auto"
    />
  </div>
);

/**
 * Mirrors the real content's own section structure (gap-4 rhythm, the same
 * border-t+pt-3/pt-4 rules before Performance/Rebate split/Archive) so the
 * sheet doesn't visibly reflow when the loading state resolves — the
 * previous version was a generic "3 bars, then 6 bars" block unrelated to
 * where real content actually lands (PART-4052 follow-up, issue 9: Claude
 * Design has no skeleton spec for this sheet, so this follows the real
 * layout instead). Kept in sync with issue 7's spacing revision — see that
 * div's own comments for why each value is what it is.
 */
const DrawerSkeletonBody = () => (
  <div
    className="flex flex-col gap-4 pt-4"
    aria-busy="true"
    data-testid="mobile-program-detail-loading"
  >
    {/* Identity — HOID only, no tag-pill placeholders (matches the program
        list card's own skeleton: not every row has a tag, and the sheet's
        status pill isn't worth a stand-in either). */}
    <SkeletonLoader height={20} width={100} variant="rounded" />

    {/* Referral link */}
    <div className="flex flex-col gap-2">
      <SkeletonLoader height={14} width={88} variant="rounded" />
      <SkeletonLoader height={40} variant="rounded" />
    </div>

    {/* Program created / Last payment / Your rebate account */}
    <div className="flex flex-col">
      <SkeletonFieldRow className="!pt-0" labelWidth={104} />
      <SkeletonFieldRow labelWidth={88} />
      <SkeletonFieldRow className="!pb-0" labelWidth={128} />
    </div>

    {/* Performance */}
    <div className="flex flex-col gap-3 border-t border-stroke-primary pt-3">
      <div className="flex items-center justify-between gap-4 py-1">
        <SkeletonLoader height={16} width={80} variant="rounded" />
        <SkeletonLoader height={28} width={104} radius="full" />
      </div>
      <div className="flex flex-col">
        <SkeletonFieldRow className="!pt-0" labelWidth={88} />
        <SkeletonFieldRow labelWidth={80} />
        <SkeletonFieldRow labelWidth={88} />
        <SkeletonFieldRow labelWidth={56} />
        <SkeletonFieldRow labelWidth={96} />
        <SkeletonFieldRow className="!pb-0" labelWidth={88} />
      </div>
    </div>

    {/* Rebate split */}
    <div className="flex flex-col gap-4 border-t border-stroke-primary pt-4">
      <SkeletonLoader height={16} width={88} variant="rounded" />
      <SkeletonLoader height={16} radius="full" />
      <div className="flex flex-col gap-4">
        <SkeletonSplitRow nameWidth={56} />
        <SkeletonSplitRow nameWidth={80} />
      </div>
    </div>

    {/* Archive */}
    <div className="border-t border-stroke-primary pt-4">
      <SkeletonLoader height={40} variant="rounded" />
    </div>
  </div>
);

type MobileProgramDetailSheetProps = {
  hoid: Hoid | null;
  onClose: () => void;
  onBack?: () => void;
  onArchive: (hoidId: string) => void;
  fetchDetail?: (
    programId: string,
    dateRange: { from: Date | null; to: Date | null },
  ) => Promise<ProgramDetailPatch>;
  /** The page's own date filter — seeds the sheet's own range on open; never written back to. */
  pageDateRange?: { from: Date | null; to: Date | null };
  /** Rendered as a "View performance" text link next to the identity title when supplied. */
  onViewAnalytics?: (hoid: Hoid) => void;
  /** Trading-report href for the "View client data" button; undefined hides it. */
  getClientDataHref?: (hoid: Hoid, dateRange: DateRange) => string | undefined;
  onNavigateToClientsReport?: (href: string) => void;
  /** Live catalogue; null falls back to the mock in `catalogue.ts`. */
  assets?: AssetGroup[] | null;
  showSubIbStatus?: boolean;
  onDismissRejectedNotification?: (partner: HoidPartner) => void;
  /** Phase 2: closed-program banner gets the new copy, style and placement. */
  phase2Enabled?: boolean;
};

/**
 * Full-screen mobile program detail sheet — the mobile presentation of the
 * desktop `ProgramDetailsDrawer.tsx`, reusing its exact data/skeleton logic
 * (per-field scoped reload, `fetchDetail`/`ProgramDetailPatch`) with stacked
 * sections instead of a side-panel + 3-column grids (PART-4052 §4.5).
 */
export const MobileProgramDetailSheet = ({
  hoid,
  onClose,
  onBack,
  onArchive,
  fetchDetail,
  pageDateRange,
  onViewAnalytics,
  getClientDataHref,
  onNavigateToClientsReport,
  assets,
  showSubIbStatus,
  onDismissRejectedNotification,
  phase2Enabled,
}: MobileProgramDetailSheetProps) => {
  const { t } = useTranslation();
  const presets = useDateRangePresets();
  const defaultRange =
    presets.find((p) => p.id === "allTime")?.range ?? presets[0]!.range;

  const [dateRange, setDateRange] = useState<DateRange>(
    pageDateRange?.from && pageDateRange.to
      ? { from: pageDateRange.from, to: pageDateRange.to }
      : defaultRange,
  );
  const [dateSheetOpen, setDateSheetOpen] = useState(false);
  const [detail, setDetail] = useState<ProgramDetailPatchState | undefined>();
  const [retryCount, setRetryCount] = useState(0);
  const hasLoadedOnceRef = useRef(false);

  // Re-seed the sheet's own range whenever a (different) program opens —
  // mirrors `ProgramDetailsDrawer`'s hoid-keyed seed effect.
  useEffect(() => {
    if (!hoid) return;
    setDateRange(
      pageDateRange?.from && pageDateRange.to
        ? { from: pageDateRange.from, to: pageDateRange.to }
        : defaultRange,
    );
    hasLoadedOnceRef.current = false;
    setDetail(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoid?.id]);

  const programApiId = hoid ? (hoid.programId ?? hoid.id) : null;
  const fromKey = dateRange.from.getTime();
  const toKey = dateRange.to.getTime();

  useEffect(() => {
    if (!programApiId || !fetchDetail) {
      setDetail(undefined);
      return;
    }
    let alive = true;
    setDetail({ status: "loading" });
    fetchDetail(programApiId, { from: new Date(fromKey), to: new Date(toKey) })
      .then((patch) => {
        if (alive) {
          hasLoadedOnceRef.current = true;
          setDetail({ status: "success", value: patch });
        }
      })
      .catch(() => {
        if (alive) setDetail({ status: "error" });
      });
    return () => {
      alive = false;
    };
  }, [programApiId, fromKey, toKey, retryCount, fetchDetail]);

  if (!hoid) return null;

  const status = programDisplayStatus(hoid);
  const isArchived = status === "Archived";

  const tradingVolume = !detail
    ? hoid.tradingVolume
    : detail.status === "success"
      ? detail.value.tradingVolume
      : null;
  const clientDataHref =
    tradingVolume && tradingVolume > 0 && onNavigateToClientsReport
      ? getClientDataHref?.(hoid, dateRange)
      : undefined;

  return (
    <>
      <BottomSheet
        // Always open here (the `!hoid` guard above already returned). The
        // date-range chip's own sheets are rendered as a sibling below, not
        // nested inside this sheet's children, so this sheet no longer needs
        // to unmount itself via `isOpen` to let them take over the screen —
        // see `MobileDateRangeChipSheets`'s own comment for why the old
        // `isOpen={!dateSheetOpen}` toggle broke "replace, don't stack"
        // instead of implementing it.
        isOpen
        onClose={onClose}
        onBack={onBack}
        title={t("subIBReport_programDetailsTitle", "Rebate program details")}
        isDraggable
        // The Performance section's sticky date-range bar (below) needs to
        // reach the true top of the scrollable body with no gap when stuck —
        // but BottomSheet's body has its own p-4, and a scroll container's own
        // top padding stays as a permanent gap above a sticky child (sticky
        // "top" is measured from the padding edge, which sits below that
        // padding, not the padding itself). !pt-0 removes that padding from
        // the scroll container itself; each branch below restores its own
        // top inset locally instead (pt-4 on the real content and the
        // skeleton, DrawerErrorBody's own py-16 already has plenty).
        contentContainerClassName="!pt-0"
      >
        {!hasLoadedOnceRef.current && detail?.status === "error" ? (
          <DrawerErrorBody onRetry={() => setRetryCount((n) => n + 1)} />
        ) : !hasLoadedOnceRef.current && detail?.status === "loading" ? (
          <DrawerSkeletonBody />
        ) : (
          <div className="flex flex-col gap-4 pt-4">
            {/* Identity + status. "View performance" lives here, not as a
              bottom-of-Performance-section button — canvas's m-pd__namerow
              renders it inline with the title (m-pd__analytics--titlerow:
              margin-left: auto), a small text link, not a full-width button. */}
            <div className="flex flex-wrap items-center gap-2">
              <Typo element="span" type="labelMD" textColor="text-text-primary">
                {hoid.rebateProgramLabel}
              </Typo>
              {isRecentlyAdded(hoid.createdAt) && (
                <StatusPill
                  status="New"
                  label={t("subIBReport_newTag", "New")}
                />
              )}
              <StatusPill
                status={status}
                label={
                  status === "Archived"
                    ? t("subIBReport_statusArchived", "Closed")
                    : undefined
                }
              />
              {onViewAnalytics && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onViewAnalytics(hoid);
                  }}
                  className="ml-auto flex shrink-0 items-center gap-1.5 text-text-brand-default hover:underline focus-visible:rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stroke-focused"
                >
                  <ChartLineIcon
                    className="h-4 w-4 shrink-0"
                    aria-hidden="true"
                  />
                  <Typo
                    element="span"
                    type="labelXS"
                    textColor="text-text-brand-default"
                  >
                    {t("subIBReport_viewPerformance", "View performance")}
                  </Typo>
                </button>
              )}
            </div>

            {isArchived && phase2Enabled && (
              <InlineNotification variant="tonal" icon={BaseArchiveIcon}>
                {t(
                  "subIBReport_archivedLinkExplainerPhase2",
                  "You still share rebates with Sub IBs on clients who joined before this program closed. Rebates from clients who join after the closure are 100% yours.",
                )}
              </InlineNotification>
            )}

            {/* Referral link */}
            <div className="flex flex-col gap-2">
              <Typo
                element="span"
                type="bodySM"
                textColor="text-text-secondary"
              >
                {t("subIBReport_referralLink", "Referral link")}
              </Typo>
              {/* Canvas `.hcard__link-url`: "Long links wrap to a new line
                rather than truncating" — and mobile's `.m-pd
                .hcard__link-input` drops the fixed 40px height to `auto`
                (min-height 40px) so the pill can grow to fit the wrapped
                text. */}
              <div className="flex min-h-10 items-center gap-2 rounded-lg bg-surface-tonal-default py-2 pl-3.5 pr-1">
                <Typo
                  element="span"
                  type="bodySM"
                  className="min-w-0 flex-1 break-words"
                >
                  {hoid.referralLink}
                </Typo>
                <CopyButton
                  text={hoid.referralLink}
                  copyLabel={t(
                    "subIBReport_copyReferralLink",
                    "Copy referral link",
                  )}
                  copiedLabel={t("subIBReport_copied", "Copied")}
                  showCopiedTooltip={false}
                  className="!h-8 !w-8"
                />
              </div>
              {isArchived && !phase2Enabled && (
                <InlineNotification variant="neutral" icon={BaseArchiveIcon}>
                  {t(
                    "subIBReport_archivedLinkExplainer",
                    "Clients who joined before this program closed continue to share rebates with your sub IBs. Rebates from clients who join after are 100% yours.",
                  )}
                </InlineNotification>
              )}
            </div>

            {/* Program created / Last payment / Rebate account — canvas's
              SmPdLine helper renders both key and value as typo-body-sm, so
              these override MobileFieldRow's card-style Jakarta value. */}
            <div className="flex flex-col">
              {/* !pt-0: the group's own gap-4 (16px) from the outer container
                already nets the referral-link→first-row gap on its own —
                this row's default py-2 top padding would double it to 24px. */}
              <MobileFieldRow
                className="!pt-0"
                label={t("subIBReport_programCreated", "Program created")}
                value={fmtDate(hoid.createdAt)}
                valueType="bodySM"
              />
              <MobileFieldRow
                label={t("subIBReport_colLastPayment", "Last payment")}
                info={
                  <MobileInfoTrigger
                    label={t("subIBReport_colLastPayment", "Last payment")}
                    text={t(
                      "subIBReport_lastPaymentInfo",
                      "Most recent payout date for this rebate program. Not affected by the selected date range.",
                    )}
                  />
                }
                value={
                  hoid.lastPayment ? (
                    fmtDate(hoid.lastPayment)
                  ) : (
                    <span className="text-text-disabled">{fmtDate(null)}</span>
                  )
                }
                valueType="bodySM"
              />
              {/* !pb-0: this row's default py-2 bottom padding would stack
                with the outer gap-4 below, doubling the divider gap to 24px
                (see the Performance section's own top padding for the other
                half of that gap). */}
              <MobileFieldRow
                className="!pb-0"
                label={t(
                  "subIBReport_yourRebateAccount",
                  "Your rebate account",
                )}
                info={
                  <MobileInfoTrigger
                    label={t(
                      "subIBReport_yourRebateAccount",
                      "Your rebate account",
                    )}
                    text={t(
                      "subIBReport_rebateAccountInfo",
                      "The account where this program's rebate earnings are paid to",
                    )}
                  />
                }
                value={hoid.accountNumber}
                valueType="bodySM"
              />
            </div>

            {/* Performance. pt-3 (12px) + the sticky header's own py-1 top
              (4px) nets the 16px divider→title gap; gap-3 (12px) + the
              header's own py-1 bottom (4px) nets the 16px title→first-row
              gap (that row's own top padding is zeroed below). */}
            <div className="flex flex-col gap-3 border-t border-stroke-primary pt-3">
              <div className="sticky top-0 z-10 flex items-center justify-between gap-4 bg-surface-primary py-1">
                <Typo
                  element="span"
                  type="labelSM"
                  textColor="text-text-primary"
                >
                  {t("subIBReport_performance", "Performance")}
                </Typo>
                <MobileDateRangeChip
                  value={dateRange}
                  open={dateSheetOpen}
                  onOpen={() => setDateSheetOpen(true)}
                />
              </div>
              <div className="flex flex-col">
                <MobileFieldRow
                  className="!pt-0"
                  label={t("subIBReport_colTotalEarning", "Total earnings")}
                  info={
                    <MobileInfoTrigger
                      label={t("subIBReport_colTotalEarning", "Total earnings")}
                      text={t(
                        "subIBReport_totalEarningInfo",
                        "The total gross rebate earned across all programs shared between both you and your Sub IBs",
                      )}
                    />
                  }
                  value={renderPerformanceField(
                    hoid.totalRebate,
                    detail,
                    (p) => p.totalRebate,
                    fmtVolMln,
                  )}
                  valueType="bodySM"
                />
                <MobileFieldRow
                  label={t("subIBReport_colYourPayout", "Your payout")}
                  value={renderPerformanceField(
                    hoid.myPayout,
                    detail,
                    (p) => p.myPayout,
                    fmtVolMln,
                  )}
                  valueType="bodySM"
                />
                <MobileFieldRow
                  label={t("subIBReport_subIbPayout", "Sub IB payout")}
                  value={renderPerformanceField(
                    hoid.subIbPayout,
                    detail,
                    (p) => p.subIbPayout,
                    fmtVolMln,
                  )}
                  valueType="bodySM"
                />
                <MobileFieldRow
                  label={t("subIBReport_uniqueClients", "Clients")}
                  info={
                    <MobileInfoTrigger
                      label={t("subIBReport_uniqueClients", "Clients")}
                      text={t(
                        "subIBReport_clientsInfo",
                        "All-time client data under this rebate program, regardless of the selected date range",
                      )}
                    />
                  }
                  value={fmtInt(hoid.uniqueClients)}
                  valueType="bodySM"
                />
                <MobileFieldRow
                  label={t("subIBReport_clientAccounts", "Client accounts")}
                  info={
                    <MobileInfoTrigger
                      label={t("subIBReport_clientAccounts", "Client accounts")}
                      text={t(
                        "subIBReport_clientAccountsInfo",
                        "All-time client data under this rebate program, regardless of the selected date range",
                      )}
                    />
                  }
                  value={fmtInt(hoid.clientAccounts)}
                  valueType="bodySM"
                />
                {/* !pb-0: default py-2 bottom padding would stack with the
                  outer sheet gap-4 below, doubling the next divider gap. */}
                <MobileFieldRow
                  className="!pb-0"
                  label={t("subIBReport_tradingVolume", "Trading volume")}
                  value={renderPerformanceField(
                    hoid.tradingVolume,
                    detail,
                    (p) => p.tradingVolume,
                    fmtVolMln,
                  )}
                  valueType="bodySM"
                />
              </div>
              {clientDataHref && (
                <ButtonV2
                  data-testid="rebate_program_detail_sheet_view_client_data_button"
                  variant="tonal"
                  size="sm"
                  className="w-full"
                  iconR={<ArrowRightIcon />}
                  onClick={() => {
                    onClose();
                    onNavigateToClientsReport?.(clientDataHref);
                  }}
                >
                  {t("subIBReport_viewClientData", "View client data")}
                </ButtonV2>
              )}
            </div>

            {/* Rebate split. pt-4 (16px) alone nets the divider→title gap —
              unlike Performance, this title has no wrapping header padding
              of its own to share the load with. */}
            <div className="flex flex-col gap-4 border-t border-stroke-primary pt-4">
              <Typo element="span" type="labelSM" textColor="text-text-primary">
                {t("subIBReport_colSplit", "Rebate split")}
              </Typo>
              <div className="flex h-4 gap-0.5 overflow-hidden rounded-full">
                <div
                  className={SPLIT_COLORS[0]}
                  style={{ width: `${hoid.masterSplitPct}%`, minWidth: "4px" }}
                />
                {hoid.partners.map((p, i) => (
                  <div
                    key={p.subIbId}
                    className={SPLIT_COLORS[(i + 1) % SPLIT_COLORS.length]}
                    style={{ width: `${p.splitPct}%`, minWidth: "4px" }}
                  />
                ))}
              </div>
              {/* Canvas's `.pd-split__row` is a real 3-column grid
                (`minmax(0,1fr) 52px 100px` on mobile, 16px gap) with its own
                "IB"/"Split %"/"Payout" header row — not a label→value
                MobileFieldRow, which only gives 2 columns. Header row uses
                -mb-2 (canvas: `.pd-split__row--head { margin-bottom: -8px }`)
                to sit closer to the first data row than the 16px row gap. */}
              <div className="flex flex-col gap-4">
                <div className="-mb-2 grid grid-cols-[minmax(0,1fr)_52px_100px] gap-4">
                  <Typo
                    element="span"
                    type="bodyXS"
                    textColor="text-text-secondary"
                  >
                    {t("subIBReport_splitColIb", "IB")}
                  </Typo>
                  <Typo
                    element="span"
                    type="bodyXS"
                    textColor="text-text-secondary"
                    className="text-right"
                  >
                    {t("subIBReport_splitColumn", "Split %")}
                  </Typo>
                  <Typo
                    element="span"
                    type="bodyXS"
                    textColor="text-text-secondary"
                    className="text-right"
                  >
                    {t("subIBReport_splitColPayout", "Payout")}
                  </Typo>
                </div>

                <div className="grid grid-cols-[minmax(0,1fr)_52px_100px] items-center gap-4">
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span
                      className={`h-4 w-4 shrink-0 rounded ${SPLIT_COLORS[0]}`}
                      aria-hidden="true"
                    />
                    <Typo
                      element="span"
                      type="bodySM"
                      textColor="text-text-primary"
                      className="truncate"
                    >
                      {t("subIBReport_you", "You")}
                    </Typo>
                  </span>
                  <Typo
                    element="span"
                    type="labelSM"
                    textColor="text-text-primary"
                    className="whitespace-nowrap text-right"
                  >{`${hoid.masterSplitPct}%`}</Typo>
                  <Typo
                    element="span"
                    type="bodySM"
                    textColor="text-text-primary"
                    className="whitespace-nowrap text-right"
                  >
                    {renderPerformanceField(
                      hoid.myPayout,
                      detail,
                      (p) => p.myPayout,
                      fmtVolMln,
                    )}
                  </Typo>
                </div>

                {hoid.partners.map((p, i) => (
                  <div
                    key={p.subIbId}
                    className="grid grid-cols-[minmax(0,1fr)_52px_100px] items-center gap-4"
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span
                        className={`h-4 w-4 shrink-0 rounded ${SPLIT_COLORS[(i + 1) % SPLIT_COLORS.length]}`}
                        aria-hidden="true"
                      />
                      <Typo
                        element="span"
                        type="bodySM"
                        textColor="text-text-primary"
                        className="truncate"
                      >
                        {p.name}
                      </Typo>
                      {showSubIbStatus && (
                        <SubIbStatusTag
                          status={p.ibStatus}
                          isMobile
                          hasRejectedNotification={hasUndismissedRejection(p)}
                          onRejectedNotificationSeen={() =>
                            onDismissRejectedNotification?.(p)
                          }
                        />
                      )}
                    </span>
                    <Typo
                      element="span"
                      type="labelSM"
                      textColor="text-text-primary"
                      className="whitespace-nowrap text-right"
                    >{`${p.splitPct}%`}</Typo>
                    <Typo
                      element="span"
                      type="bodySM"
                      textColor="text-text-primary"
                      className="whitespace-nowrap text-right"
                    >
                      {renderPerformanceField(
                        p.payout,
                        detail,
                        (patch) => patch.subIbPayoutById[p.subIbId] ?? p.payout,
                        fmtVolMln,
                      )}
                    </Typo>
                  </div>
                ))}
              </div>
            </div>

            {/* Rebate split override — same collapsible section as the desktop
              drawer (`ProgramDetailsDrawer.tsx`); canvas's `OverrideDrawerSection`
              is shared across both breakpoints, and the component itself has
              no desktop-only sizing (flex-wrap, self-contained), so it's
              reused here rather than forked. Renders null when there are no
              symbol overrides.
              No divider before this one (unlike Performance/Rebate split/
              Archive) — just the outer sheet's own gap-4 (16px). The shared
              component's own `-mt-2` (tuned for the desktop drawer's gap
              unit) is cancelled so it doesn't eat into that 16px. */}
            <RebateSplitOverrideSection
              hoid={hoid}
              assets={assets}
              className="!mt-0"
            />

            {/* Archive. pt-4 (16px) alone nets the divider→button gap, same
              reasoning as Rebate split's own pt-4 above. */}
            {!isArchived && (
              <div className="border-t border-stroke-primary pt-4">
                <ButtonV2
                  variant="negative"
                  size="sm"
                  className="w-full"
                  onClick={() => onArchive(hoid.id)}
                >
                  <div className="flex items-center gap-2">
                    <BaseArchiveIcon className="h-4 w-4" aria-hidden="true" />
                    {t(
                      "subIBReport_archiveThisProgram",
                      "Close this rebate program",
                    )}
                  </div>
                </ButtonV2>
              </div>
            )}
          </div>
        )}
      </BottomSheet>

      <MobileDateRangeChipSheets
        value={dateRange}
        onChange={setDateRange}
        open={dateSheetOpen}
        onClose={() => setDateSheetOpen(false)}
        name={`sm-pd-date-${hoid.id}`}
      />
    </>
  );
};
